import React, { ReactNode, useEffect, useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useTranslation } from "react-i18next";
import { Box, Typography, useTheme } from "@mui/material";
import { WithLoadingSpinner } from "@components";
import { ChartWrapper } from "../common";
import { useChartOptions } from "./useChartOptions";

type Props = {
  chartData: [number, number][];
  lineColor: string;
  isLoading?: boolean;
  noDataMessage?: ReactNode;
  width?: number;
  height?: number;
};

// @ts-ignore
Highcharts.seriesTypes.area.prototype.getPointSpline =
  // @ts-ignore
  Highcharts.seriesTypes.spline.prototype.getPointSpline;

export const SimpleLineChart = ({
  chartData,
  isLoading,
  noDataMessage,
  lineColor,
  width,
  height,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const chartOptions = useChartOptions(chartData, lineColor, width, height);

  useEffect(() => {
    chartComponentRef.current?.chart?.reflow();
  }, [chartOptions]);

  return (
    <Box pb={1} height={height}>
      <WithLoadingSpinner
        isLoading={Boolean(isLoading)}
        width={width}
        spinnerWidth={25}
      >
        {/* 1 because we need at least 2 points to draw a line */}
        {chartData.length <= 1 && !isLoading ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height={height}
          >
            <Typography
              variant="body3"
              color={theme.palette.text.secondary}
              width={width}
              textAlign="center"
            >
              {noDataMessage ? noDataMessage : t("depotPage.chart.noData")}
            </Typography>
          </Box>
        ) : (
          <ChartWrapper>
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              ref={chartComponentRef}
            />
          </ChartWrapper>
        )}
      </WithLoadingSpinner>
    </Box>
  );
};
